/**
 * @file store.ts
 * @description Zustand store for the API Visual Debugger.
 * Tracks both request state and the active debugger configuration.
 *
 * As of the SSR bridge update this store holds two parallel request lists:
 *   - `requests`    — Client-side requests captured directly in the browser.
 *   - `ssrRequests` — SSR requests polled from /api/dev/api-debugger every second.
 *
 * The overlay combines them via a `mergedRequests` selector, sorted newest-first.
 *
 * Replaces: src/store/useApiDebuggerStore.ts
 */

import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';

import { ApiRequest, DebuggerConfig, DEFAULT_CONFIG } from './types';

/** sessionStorage key used when `clearOnReload=false`. */
const SESSION_STORAGE_KEY = '__api-debugger-store';

/**
 * Dynamic `PersistStorage` that decides — on each call — whether to persist
 * to sessionStorage or short-circuit to a no-op based on the currently
 * published `clearOnReload` flag on `globalThis`.
 *
 * Why not `createJSONStorage`?
 *   `createJSONStorage` wraps `setItem` with a `JSON.stringify(value)` call
 *   **before** it reaches the backing store. That stringify — a full
 *   serialization of a cap-sized `requests` array where each entry carries
 *   a multi-KB raw stack string — is the exact per-`set()` cost we're
 *   trying to eliminate. Implementing `PersistStorage` directly lets us
 *   skip the stringify entirely when it isn't needed.
 *
 * Why dynamic instead of a one-shot factory choice?
 *   The `persist` middleware evaluates its `storage` option once at store
 *   creation time — which is at module import, **before**
 *   `createDebugInterceptor` publishes the resolved `clearOnReload` flag
 *   onto `globalThis`. A dynamic adapter checks the flag on every I/O so
 *   the user's config always wins.
 */
type PersistedShape = { requests: ApiRequest[] };

function isPersistDisabled(): boolean {
  const clearOnReload =
    (globalThis as any).__apiDebuggerClearOnReload ??
    DEFAULT_CONFIG.clearOnReload;
  return clearOnReload === true;
}

const DYNAMIC_PERSIST_STORAGE: PersistStorage<PersistedShape> = {
  getItem: (name) => {
    if (isPersistDisabled()) return null;
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(name);
      return raw ? (JSON.parse(raw) as { state: PersistedShape; version?: number }) : null;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    if (isPersistDisabled()) return;
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(name, JSON.stringify(value));
    } catch {
      // Ignore storage errors (quota, private mode, etc.).
    }
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(name);
    } catch {
      // Ignore.
    }
  },
};

// ─── State & Action Types ─────────────────────────────────────────────────────

type ApiDebuggerState = {
  /** Live Client-side request log, newest first. Capped at config.maxRequests. */
  requests: ApiRequest[];

  /**
   * SSR requests polled from /api/dev/api-debugger.
   * Replaced wholesale on each successful poll tick.
   */
  ssrRequests: ApiRequest[];

  /** Active configuration. Seeded by createDebugInterceptor() on mount. */
  config: DebuggerConfig;
};

type ApiDebuggerActions = {
  /**
   * Adds a new Client-side request entry to the log.
   * Respects the maxRequests cap from config.
   */
  addRequest: (request: ApiRequest) => void;

  /**
   * Patches an existing Client-side request by ID (used in onResponse to update
   * status, statusCode, and duration).
   */
  updateRequest: (
    id: string,
    patch: Partial<Pick<ApiRequest, 'status' | 'statusCode' | 'duration'>>,
  ) => void;

  /**
   * Applies a batch of adds and updates in a single `set()` call.
   *
   * This is the hot-path entry point used by the interceptor's
   * microtask-coalesced flush. It replaces N discrete `addRequest` /
   * `updateRequest` calls (each of which would trigger its own subscriber
   * notification and `persist` serialization) with a single write, which
   * is what keeps the store cheap under bursts of concurrent openapi-fetch
   * calls.
   */
  applyBatch: (
    adds: ApiRequest[],
    updates: Array<{
      id: string;
      patch: Partial<Pick<ApiRequest, 'status' | 'statusCode' | 'duration'>>;
    }>,
  ) => void;

  /**
   * Replaces the SSR request list with a fresh snapshot from the server.
   * Called by the polling effect in <ApiDebuggerOverlay />.
   */
  setSsrRequests: (requests: ApiRequest[]) => void;

  /**
   * Clears all recorded Client-side requests.
   * The overlay is responsible for also sending DELETE /api/dev/api-debugger
   * to clear the server-side cache and reset ssrRequests.
   */
  clearRequests: () => void;

  /**
   * Merges a partial config patch into the active configuration.
   * Called by createDebugInterceptor() to seed initial config from call-site options.
   */
  setConfig: (patch: Partial<DebuggerConfig>) => void;
};

// ─── Store ───────────────────────────────────────────────────────────────────

/**
 * The store is always wrapped in the `persist` middleware, but rehydration is
 * gated on `config.clearOnReload`:
 *   - `clearOnReload: true`  (default) — persisted state is dropped on every
 *     fresh page load, so the debugger starts empty.
 *   - `clearOnReload: false` — persisted `requests` are restored from
 *     sessionStorage so they survive reloads within the same tab.
 *
 * The active `clearOnReload` value is read from a globalThis handshake set
 * by `createDebugInterceptor` before the store is first hydrated. This avoids
 * a circular dependency (interceptor → store → interceptor) at module load.
 */
export const useApiDebuggerStore = create<
  ApiDebuggerState & ApiDebuggerActions
>()(
  persist(
    (set) => ({
      requests: [],
      ssrRequests: [],
      config: { ...DEFAULT_CONFIG },

      addRequest: (request) =>
        set((state) => {
          const cap = state.config.maxRequests;
          return {
            requests: [request, ...state.requests].slice(0, cap),
          };
        }),

      updateRequest: (id, patch) =>
        set((state) => {
          // Fast path: findIndex + slice/splice avoids allocating a new
          // object for every element the way `.map()` does. On a full
          // 100-entry buffer this is ~50× less garbage per response tick.
          const idx = state.requests.findIndex((r) => r.id === id);
          if (idx === -1) return state;
          const next = state.requests.slice();
          next[idx] = { ...next[idx], ...patch };
          return { requests: next };
        }),

      applyBatch: (adds, updates) =>
        set((state) => {
          if (adds.length === 0 && updates.length === 0) return state;

          let next = state.requests;
          let mutated = false;

          if (adds.length > 0) {
            // Prepend newest-first: reverse the batch so within-batch order
            // is preserved (later push → older within the burst).
            const cap = state.config.maxRequests;
            next = adds.concat(next);
            if (next.length > cap) next = next.slice(0, cap);
            mutated = true;
          }

          if (updates.length > 0) {
            // Build an index map once; O(N + U) instead of O(N * U).
            const indexById = new Map<string, number>();
            for (let i = 0; i < next.length; i++) indexById.set(next[i].id, i);

            let cloned = mutated ? next : null;
            for (const { id, patch } of updates) {
              const idx = indexById.get(id);
              if (idx === undefined) continue;
              if (!cloned) cloned = next.slice();
              cloned[idx] = { ...cloned[idx], ...patch };
            }
            if (cloned) {
              next = cloned;
              mutated = true;
            }
          }

          return mutated ? { requests: next } : state;
        }),

      setSsrRequests: (requests) =>
        set((state) => {
          // Skip state update if the incoming snapshot is structurally identical
          // to the current one. Without this guard, the 1s polling loop would
          // hand the store a new array reference every tick, forcing every
          // subscribed component (the entire overlay) to re-render — even when
          // no SSR activity has occurred.
          //
          // Cheap field-level comparison instead of `JSON.stringify`:
          // each SSR entry carries a multi-KB `stackTrace` string, and
          // running `stringify` on both arrays every 1000ms produces
          // sustained GC pressure. The only fields the overlay actually
          // renders reactively are id/status/statusCode/duration — the
          // stack trace is immutable per-id — so comparing just those
          // is sufficient to detect meaningful changes.
          const prev = state.ssrRequests;
          if (prev.length === requests.length) {
            let identical = true;
            for (let i = 0; i < prev.length; i++) {
              const a = prev[i];
              const b = requests[i];
              if (
                a.id !== b.id ||
                a.status !== b.status ||
                a.statusCode !== b.statusCode ||
                a.duration !== b.duration
              ) {
                identical = false;
                break;
              }
            }
            if (identical) return state;
          }
          return { ssrRequests: requests };
        }),

      clearRequests: () => set({ requests: [], ssrRequests: [] }),

      setConfig: (patch) =>
        set((state) => ({
          config: { ...state.config, ...patch },
        })),
    }),
    {
      name: SESSION_STORAGE_KEY,
      // ⚠️ PERFORMANCE: `persist` calls `storage.setItem` **synchronously**
      // on every `set()` — which under bursts of concurrent openapi-fetch
      // requests means N × `JSON.stringify` of the whole `requests` array
      // (each entry carries a multi-KB raw stack string). `sessionStorage`
      // is main-thread-blocking.
      //
      // The debugger's default is `clearOnReload: true`, in which case
      // the `merge` fn below unconditionally throws the persisted blob
      // away on next mount. So when the flag is true, persistence is
      // pure overhead — we swap in a no-op Storage that skips all
      // serialization entirely. Persistence is only performed when the
      // caller explicitly opts in with `clearOnReload: false`.
      storage: DYNAMIC_PERSIST_STORAGE,
      // Only persist the client-side `requests` list — SSR requests come from
      // the live server cache, and `config` is seeded fresh each mount.
      partialize: (state) => ({ requests: state.requests }),
      // ⚠️ Hydration race fix:
      // `persist` normally hydrates synchronously during `create()` — which
      // happens at module import time, before the host app has a chance to
      // call `createDebugInterceptor(config)`. At that moment
      // `globalThis.__apiDebuggerClearOnReload` is still undefined, so both
      // the storage adapter and the `merge` fn fall back to
      // `DEFAULT_CONFIG.clearOnReload` (true) and wipe sessionStorage —
      // making `clearOnReload: false` silently no-op.
      //
      // With `skipHydration: true`, hydration is deferred until
      // `createDebugInterceptor` explicitly calls
      // `useApiDebuggerStore.persist.rehydrate()` *after* publishing the
      // resolved config to globalThis, so the user's flag always wins.
      skipHydration: true,
      merge: (persistedState, currentState) => {
        const clearOnReload =
          (globalThis as any).__apiDebuggerClearOnReload ??
          DEFAULT_CONFIG.clearOnReload;
        if (clearOnReload) {
          // Wipe the persisted blob so it doesn't linger between sessions.
          try {
            if (typeof window !== 'undefined') {
              window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
            }
          } catch {
            // Ignore storage errors (e.g. private-mode quota).
          }
          return currentState;
        }
        return {
          ...currentState,
          ...(persistedState as Partial<ApiDebuggerState>),
        };
      },
    },
  ),
);

// ─── Convenience selector types (re-exported for consumers) ───────────────────
export type { ApiRequest, DebuggerConfig } from './types';
export type { ApiRequestStatus, ApiEnvironment, StackFrame } from './types';
